/**
 * CommentRow — vibe comment with optional inline replies + actions.
 * Extracted from VibeDetail.js to keep the parent file under the
 * babel-traverse stack limit. Threading is intentionally flat (1 level).
 */
import { Heart, MoreHorizontal } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function ReplyItem({
  reply, currentUser, canDeleteParent, flashId, commentRefs,
  navigate, onLike, onReply, setMenuFor, parentId,
}) {
  const isAuthor = currentUser?.id === reply.user?.id;
  const canDelete = isAuthor || canDeleteParent;
  return (
    <div
      ref={el => { if (el) commentRefs.current[reply.id] = el; }}
      data-testid={`comment-${reply.id}`}
      className={`flex items-start gap-2.5 p-2.5 rounded-2xl transition-all ml-9 ${
        flashId === reply.id ? 'bg-[#B6E600]/20 ring-2 ring-[#B6E600]' : 'bg-white border border-black/[0.04]'
      }`}
    >
      <button
        onClick={() => reply.user?.id && navigate(`/user/${reply.user.id}`)}
        className="flex-shrink-0"
        aria-label="Open profile"
      >
        {reply.user?.profile_photo ? (
          <img loading="lazy" decoding="async" src={reply.user.profile_photo} alt="" className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[10px] font-bold text-[#9B9EA3]">
            {getInitials(reply.user?.display_name)}
          </div>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-semibold text-xs text-[#111111]">{reply.user?.display_name}</span>
          <span className="text-[10px] text-[#9B9EA3]">
            {reply.created_at ? formatDistanceToNow(new Date(reply.created_at), { addSuffix: true }) : ''}
          </span>
        </div>
        <p className="text-sm text-[#2A2A2A] mt-0.5 whitespace-pre-wrap break-words">{reply.text}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <button
            data-testid={`comment-like-${reply.id}`}
            onClick={() => onLike(reply, parentId)}
            className="flex items-center gap-1 text-[11px] active:scale-95"
            aria-label={reply.is_liked ? 'Unlike' : 'Like'}
          >
            <Heart
              size={13}
              strokeWidth={2.4}
              className={reply.is_liked ? 'text-[#FF3B30]' : 'text-[#9B9EA3]'}
              fill={reply.is_liked ? '#FF3B30' : 'none'}
            />
            <span className={`font-bold ${reply.is_liked ? 'text-[#FF3B30]' : 'text-[#9B9EA3]'}`}>
              {reply.likes_count || 0}
            </span>
          </button>
          <button
            data-testid={`comment-reply-${reply.id}`}
            onClick={() => onReply(reply, parentId)}
            className="text-[11px] font-bold text-[#3F5200] active:scale-95 hover:underline"
          >
            Reply
          </button>
        </div>
      </div>
      <button
        data-testid={`comment-menu-${reply.id}`}
        onClick={() => setMenuFor({ ...reply, _canDelete: canDelete, parent_id: parentId })}
        className="flex-shrink-0 p-1 text-[#9B9EA3]"
        aria-label="Comment options"
      >
        <MoreHorizontal size={18} />
      </button>
    </div>
  );
}

export default function CommentRow({
  comment, canDelete, currentUser, flashId, commentRefs,
  navigate, onLike, onReply, setMenuFor, replies,
  hasMoreReplies, onToggleReplies, isExpanded, expandedTotal,
}) {
  return (
    <>
      <div
        ref={el => { if (el) commentRefs.current[comment.id] = el; }}
        data-testid={`comment-${comment.id}`}
        className={`flex items-start gap-2.5 p-2.5 rounded-2xl transition-all ${
          flashId === comment.id ? 'bg-[#B6E600]/20 ring-2 ring-[#B6E600]' : 'bg-white border border-black/[0.04]'
        }`}
      >
        <button
          onClick={() => comment.user?.id && navigate(`/user/${comment.user.id}`)}
          className="flex-shrink-0"
          aria-label="Open profile"
        >
          {comment.user?.profile_photo ? (
            <img loading="lazy" decoding="async" src={comment.user.profile_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[10px] font-bold text-[#9B9EA3]">
              {getInitials(comment.user?.display_name)}
            </div>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-xs text-[#111111]">{comment.user?.display_name}</span>
            <span className="text-[10px] text-[#9B9EA3]">
              {comment.created_at ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true }) : ''}
            </span>
          </div>
          <p className="text-sm text-[#2A2A2A] mt-0.5 whitespace-pre-wrap break-words">{comment.text}</p>
          <div className="flex items-center gap-3 mt-1.5">
            <button
              data-testid={`comment-like-${comment.id}`}
              onClick={() => onLike(comment, null)}
              className="flex items-center gap-1 text-[11px] active:scale-95"
              aria-label={comment.is_liked ? 'Unlike' : 'Like'}
            >
              <Heart
                size={13}
                strokeWidth={2.4}
                className={comment.is_liked ? 'text-[#FF3B30]' : 'text-[#9B9EA3]'}
                fill={comment.is_liked ? '#FF3B30' : 'none'}
              />
              <span className={`font-bold ${comment.is_liked ? 'text-[#FF3B30]' : 'text-[#9B9EA3]'}`}>
                {comment.likes_count || 0}
              </span>
            </button>
            <button
              data-testid={`comment-reply-${comment.id}`}
              onClick={() => onReply(comment, null)}
              className="text-[11px] font-bold text-[#3F5200] active:scale-95 hover:underline"
            >
              Reply
            </button>
          </div>
        </div>
        <button
          data-testid={`comment-menu-${comment.id}`}
          onClick={() => setMenuFor({ ...comment, _canDelete: canDelete, parent_id: null })}
          className="flex-shrink-0 p-1 text-[#9B9EA3]"
          aria-label="Comment options"
        >
          <MoreHorizontal size={18} />
        </button>
      </div>

      {replies && replies.length > 0 && (
        <div className="space-y-2 mt-1" data-testid={`replies-of-${comment.id}`}>
          {replies.map(r => (
            <ReplyItem
              key={r.id}
              reply={r}
              currentUser={currentUser}
              canDeleteParent={canDelete}
              flashId={flashId}
              commentRefs={commentRefs}
              navigate={navigate}
              onLike={onLike}
              onReply={onReply}
              setMenuFor={setMenuFor}
              parentId={comment.id}
            />
          ))}
        </div>
      )}

      {(hasMoreReplies || isExpanded) && (
        <button
          data-testid={`replies-toggle-${comment.id}`}
          onClick={() => onToggleReplies(comment.id)}
          className="ml-12 mt-1 text-[11px] font-bold text-[#9B9EA3] active:scale-95"
        >
          {isExpanded
            ? '— Hide replies'
            : `— View ${expandedTotal > 0 ? expandedTotal : ((comment.replies_count || 0) - (comment.replies?.length || 0))} more ${(expandedTotal === 1 || ((comment.replies_count || 0) - (comment.replies?.length || 0)) === 1) ? 'reply' : 'replies'}`}
        </button>
      )}
    </>
  );
}
